import { getSupabase, isSupabaseConfigured } from './client';
import {
  connectGoogle,
  disconnectGoogle,
  takeIdToken,
  getOrRefreshProfile,
} from '../googleAuth';
import { hasSession } from '../tokenStore';
import { t } from '../language';
import { SUPPORT_EMAIL } from '../support';
import type { AccountPlan, AppUser, AuthState, SignInResult, UserRole } from '../../shared/types';

const SIGNED_OUT: AuthState = { signedIn: false, calendarConnected: false };

/**
 * Google ile giriş — tek adımda hem kimlik hem takvim.
 *
 * Akış:
 *  1. Google PKCE onayı alınır. İstenen kapsamlar `openid email profile` +
 *     `calendar.readonly`; yani
 *     kullanıcı girişi onaylarken takvim iznini de
 *     vermiş oluyor. Takvim bağlantısı bu yüzden "zorunlu" — ayrı bir adım
 *     değil, girişin parçası.
 *  2. Google'ın döndürdüğü id_token ile Supabase oturumu açılır.
 *  3. profiles tablosundan rol okunur (Ayarlar ekranı yalnızca admin'e açık).
 *
 * Supabase panelinde yapılması gereken tek ayar: Authentication → Providers →
 * Google → "Authorized Client IDs" alanına bu uygulamanın
 * GOOGLE_CLIENT_ID değeri eklenmeli. Aksi hâlde id_token'ın
 * hedef kitlesi (aud) tanınmaz ve giriş reddedilir.
 */
export async function signInWithGoogle(): Promise<SignInResult> {
  if (!isSupabaseConfigured()) {
    return {
      ok: false,
      error: t(
        'Supabase yapılandırılmamış — kök .env dosyasına SUPABASE_URL ve SUPABASE_ANON_KEY ekleyin.',
        'Supabase is not configured — add SUPABASE_URL and SUPABASE_ANON_KEY to the root .env file.'
      ),
    };
  }

  const googleResult = await connectGoogle();
  if (!googleResult.ok) {
    return { ok: false, error: describeGoogleError(googleResult.error) };
  }

  const { idToken, accessToken } = takeIdToken();
  if (!idToken) {
    return {
      ok: false,
      error: t(
        'Google kimlik belirteci alınamadı. OAuth istemcisinde "openid" kapsamının açık olduğundan emin olun.',
        'Could not get a Google identity token. Make sure the "openid" scope is enabled on the OAuth client.'
      ),
    };
  }

  const { error } = await getSupabase().auth.signInWithIdToken({
    provider: 'google',
    token: idToken,
    access_token: accessToken ?? undefined,
  });

  if (error) {
    console.error('[auth] Supabase signInWithIdToken başarısız:', error.message);
    // Kapalı beta: prod projesindeki "Before User Created" hook'u davetsiz
    // e-postayı reddeder (supabase/migrations/0023_closed_beta.sql).
    if (error.message.includes('beta_invite_required')) {
      await disconnectGoogle();
      return {
        ok: false,
        error: t(
          `TalkNote şu an kapalı beta aşamasında ve bu Google hesabı davet listesinde değil. Davet için ${SUPPORT_EMAIL} adresine yazabilirsiniz.`,
          `TalkNote is in closed beta and this Google account is not on the invite list. To request an invite, write to ${SUPPORT_EMAIL}.`
        ),
      };
    }
    // Yönetim konsolundan askıya alınmış hesap (Supabase: "User is banned").
    if (/banned/i.test(error.message)) {
      await disconnectGoogle();
      return {
        ok: false,
        error: t(
          `Hesabın askıya alındı. Ayrıntı için ${SUPPORT_EMAIL} adresine yazabilirsin.`,
          `Your account has been suspended. For details, write to ${SUPPORT_EMAIL}.`
        ),
      };
    }
    // Ham Supabase mesajı yapılandırma ayrıntısı sızdırabiliyor.
    return {
      ok: false,
      error: t(
        'Supabase oturumu açılamadı. Google sağlayıcısının istemci kimliği panelde tanımlı mı kontrol edin.',
        'Could not start the Supabase session. Check that the Google provider’s client ID is set in the dashboard.'
      ),
    };
  }

  return { ok: true, state: await getAuthState() };
}

/** Oturumu ve Google yetkisini birlikte kapatır. */
export async function signOut(): Promise<void> {
  await getSupabase().auth.signOut().catch(() => {});
  await disconnectGoogle();
}

/**
 * Geçerli oturum durumu. Uygulama her açılışta bunu sorar; oturum yoksa
 * giriş ekranı gösterilir.
 */
export async function getAuthState(): Promise<AuthState> {
  if (!isSupabaseConfigured()) return SIGNED_OUT;

  const { data, error } = await getSupabase().auth.getSession();
  if (error || !data.session) return SIGNED_OUT;

  const calendarConnected = await hasSession();
  const user = await loadAppUser(data.session.user.id, data.session.user.email ?? '');

  return { signedIn: true, user, calendarConnected };
}

/**
 * profiles satırını okur. Satır yoksa (trigger gecikmesi) 'user' rolüyle
 * devam edilir — rol bilinmiyorsa asla admin varsayma.
 */
async function loadAppUser(userId: string, email: string): Promise<AppUser> {
  const googleProfile = await getOrRefreshProfile();

  const { data } = await getSupabase()
    .from('profiles')
    .select('display_name, role, plan, job_title, company, avatar_url')
    .eq('id', userId)
    .maybeSingle();

  const resolvedEmail = email || googleProfile?.email || '';
  return {
    id: userId,
    email: resolvedEmail,
    name: pickDisplayName(data?.display_name, resolvedEmail, googleProfile?.name),
    // Ayarlardan yüklenen fotoğraf Google fotoğrafının önüne geçer.
    avatarDataUrl: data?.avatar_url || googleProfile?.pictureDataUrl,
    jobTitle: data?.job_title || undefined,
    company: data?.company || undefined,
    role: (data?.role as UserRole) === 'admin' ? 'admin' : 'user',
    plan: (data?.plan as AccountPlan) ?? 'basic',
  };
}

/**
 * Görünen ad. Profilde kayıtlı ad önce gelir. Veritabanı tetikleyicisi yeni
 * hesapta adı e-posta adresiyle dolduruyor; o durumda ad hiç düzenlenmemiş
 * sayılır ve Google hesabındaki ad kullanılır.
 */
export function pickDisplayName(stored: string | null | undefined, email: string, googleName?: string): string {
  const custom = stored?.trim();
  if (custom && custom.toLowerCase() !== email.toLowerCase()) return custom;
  return googleName?.trim() || custom || email;
}

/**
 * Yönetici kapısı. Ayarları yalnızca arayüzde gizlemek yeterli değil —
 * IPC uçları doğrudan çağrılabilir, o yüzden yetki burada da doğrulanıyor.
 * Rol istemciden gelmiyor; her çağrıda profiles tablosundan okunuyor.
 */
export async function assertAdmin(): Promise<void> {
  const state = await getAuthState();
  if (!state.signedIn || state.user?.role !== 'admin') {
    throw new Error(t('Bu işlem için yönetici yetkisi gerekiyor.', 'This action requires administrator privileges.'));
  }
}

/** Oturum açmış kullanıcının kimliği — depo katmanı bunu kullanır. */
export async function requireUserId(): Promise<string> {
  const { data, error } = await getSupabase().auth.getSession();
  if (error || !data.session) {
    throw new Error(t('Oturum bulunamadı — lütfen tekrar giriş yapın.', 'No active session — please sign in again.'));
  }
  return data.session.user.id;
}

function describeGoogleError(code: string): string {
  switch (code) {
    case 'timeout': return t('Giriş zaman aşımına uğradı. Tekrar deneyin.', 'Sign-in timed out. Please try again.');
    case 'access_denied': return t(
      'Google izni reddedildi. Devam etmek için takvim erişimine izin vermelisiniz.',
      'Google permission was denied. You need to allow calendar access to continue.'
    );
    case 'no_refresh_token': return t('Google yenileme belirteci alınamadı. Tekrar deneyin.', 'Could not get a Google refresh token. Please try again.');
    case 'invalid_state': return t('Güvenlik doğrulaması başarısız oldu. Tekrar deneyin.', 'Security check failed. Please try again.');
    case 'browser_open_failed': return t('Tarayıcı açılamadı.', 'Could not open the browser.');
    default: return code.startsWith('token_exchange_failed')
      ? t('Google ile belirteç değişimi başarısız oldu.', 'Token exchange with Google failed.')
      : code;
  }
}
